//#region INTERFACE IAutoOrderFilterOrderSettings

/**
 * ### Interface IAutoOrderFilterOrderSettings
 * Represents filter and tool toggles for controlling visibility and inclusion criteria
 * in the auto order screen.
 *
 * @interface IAutoOrderFilterOrderSettings
 * @default {@link AutoOrderFilterOrderSettingsDefaults}
 * @description Provides toggles for filtering by purchase order status, suggestion values,
 *              and quantities. Used to customize which rows appear in the auto order view.
 *
 * @example
 * const settings: IAutoOrderFilterOrderSettings = {
 *   purchaseOrderUnset: false,
 *   purchaseOrderEqualZero: false,
 *   purchaseOrderPositive: false,
 *   suggestionPositive: false,
 *   quantityPositive: false,
 * };
 *
 * @extends Object
 */
export interface IAutoOrderFilterOrderSettings extends Object {
  /**
   * Whether to include items with a purchase order value of zero.
   * @description Filters in items where the purchase order is explicitly set to 0.
   *
   * @type {boolean}
   * @default false
   * @example false
   */
  purchaseOrderEqualZero: boolean;

  /**
   * Whether to include items with a positive purchase order value.
   * @description Enables filtering for items that have purchase order quantities greater than 0.
   *
   * @type {boolean}
   * @default false
   * @example false
   */
  purchaseOrderPositive: boolean;

  /**
   * Whether to include items with an unset purchase order.
   * @description Controls if items with no purchase order value are shown.
   *
   * @type {boolean}
   * @default false
   * @example false
   */
  purchaseOrderUnset: boolean;

  /**
   * Whether to include items with a positive quantity value.
   * @description Allows filtering of items with quantities greater than 0.
   *
   * @type {boolean}
   * @default false
   * @example false
   */
  quantityPositive: boolean;

  /**
   * Whether to include items with a positive suggestion value.
   * @description Used to filter in rows with suggested order values greater than 0.
   *
   * @type {boolean}
   * @default false
   * @example false
   */
  suggestionPositive: boolean;

  /**
   * Line color keys to show in the order view.
   * @description When non-empty, only items matching these line colour codes are shown.
   *              Mutually exclusive with `selectedLineColors`.
   *
   * @type {string[]}
   * @default []
   * @example ['3', '4']
   */
  includedLineColors?: string[];

  /**
   * Line color keys to hide from the order view.
   * @description Items matching any of these line colour codes are excluded.
   *              When empty, no line colours are hidden by this filter.
   *              Mutually exclusive with `includedLineColors`.
   *
   * @type {string[]}
   * @default []
   * @example ['1', '2', '5']
   */
  selectedLineColors?: string[];

}

//#endregion

//#region CONST AutoOrderFilterOrderSettingsDefaults

/**
 * ### Constant AutoOrderFilterOrderSettingsDefaults
 * Default values for the order filters and tools configuration.
 *
 * @constant AutoOrderFilterOrderSettingsDefaults
 * @description Provides the default state for all toggles in the `IAutoOrderFilterOrderSettings` interface.
 *              These values are used to initialize the filter logic in the auto order view.
 *
 * @example
 * const defaults = AutoOrderFilterOrderSettingsDefaults;
 * {
 *   purchaseOrderUnset: false,
 *   purchaseOrderEqualZero: false,
 *   purchaseOrderPositive: false,
 *   suggestionPositive: false,
 *   quantityPositive: false
 * }
 */
export const AutoOrderFilterOrderSettingsDefaults: IAutoOrderFilterOrderSettings = {
  purchaseOrderUnset: false,
  purchaseOrderEqualZero: false,
  purchaseOrderPositive: false,
  suggestionPositive: false,
  quantityPositive: false,
  selectedLineColors: [],
  includedLineColors: [],
};

//#endregion
